import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import SEOHead from "@/components/layout/SEOHead";
import { CheckCircle2, Loader2, XCircle, Heart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DonateSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "polling", data: null });

  useEffect(() => {
    if (!sessionId) { setStatus({ state: "error", data: null }); return; }
    let cancelled = false;
    let attempts = 0;
    const MAX = 8;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/donations/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") { setStatus({ state: "paid", data }); return; }
        if (data.status === "expired") { setStatus({ state: "expired", data }); return; }
        attempts += 1;
        if (attempts >= MAX) { setStatus({ state: "timeout", data }); return; }
        setStatus({ state: "polling", data });
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setStatus({ state: "error", data: null });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <>
      <SEOHead title="Donation Confirmation" path="/donate/success" />
      <section className="bg-white min-h-[70vh] py-20 lg:py-28 hero-glow">
        <div className="max-w-2xl mx-auto px-5 lg:px-10 text-center" data-testid="donate-success-page">
          {status.state === "polling" && (
            <>
              <Loader2 size={56} className="mx-auto text-[var(--blue)] animate-spin" />
              <h1 className="font-display text-4xl text-[var(--blue-ink)] mt-6">Confirming your gift…</h1>
              <p className="text-[var(--muted)] mt-3">Please don't close this page.</p>
            </>
          )}
          {status.state === "paid" && (
            <>
              <div className="w-20 h-20 mx-auto rounded-full bg-[var(--honey)]/25 flex items-center justify-center">
                <CheckCircle2 size={48} className="text-[var(--blue)]" />
              </div>
              <h1 className="font-display text-5xl text-[var(--blue-ink)] mt-6">Toda raba!</h1>
              <p className="mt-5 text-lg text-[var(--ink-soft)]">
                Your gift of{" "}
                <strong className="text-[var(--blue-ink)]">${status.data?.amount?.toFixed?.(2) ?? status.data?.amount}</strong>{" "}
                has been received. A receipt has been emailed.
              </p>
              <p className="mt-3 text-[var(--muted)]">Your generosity helps TIOB continue our sacred work.</p>
              <div className="mt-10 flex flex-wrap gap-4 justify-center">
                <Link to="/" className="btn-primary" data-testid="donate-success-home">Return home</Link>
                <Link to="/events" className="btn-ghost">See upcoming events</Link>
              </div>
            </>
          )}
          {status.state === "expired" && (
            <>
              <XCircle size={56} className="mx-auto text-[var(--honey)]" />
              <h1 className="font-display text-4xl text-[var(--blue-ink)] mt-6">Session expired</h1>
              <p className="mt-4 text-[var(--muted)]">Your checkout session expired. Please try again.</p>
              <Link to="/donate" className="btn-primary mt-8 inline-flex"><Heart size={16} /> Try again</Link>
            </>
          )}
          {status.state === "timeout" && (
            <>
              <Loader2 size={56} className="mx-auto text-[var(--muted)]" />
              <h1 className="font-display text-4xl text-[var(--blue-ink)] mt-6">Still processing</h1>
              <p className="mt-4 text-[var(--muted)]">Your payment is taking a bit longer than usual. Please check your email - we'll send a confirmation as soon as it clears.</p>
              <Link to="/" className="btn-primary mt-8 inline-flex">Return home</Link>
            </>
          )}
          {status.state === "error" && (
            <>
              <XCircle size={56} className="mx-auto text-red-500" />
              <h1 className="font-display text-4xl text-[var(--blue-ink)] mt-6">Something went wrong</h1>
              <p className="mt-4 text-[var(--muted)]">We couldn't confirm your donation. Please contact the office.</p>
              <Link to="/contact" className="btn-primary mt-8 inline-flex">Contact us</Link>
            </>
          )}
        </div>
      </section>
    </>
  );
}
